import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { di } from 'react-magnetic-di';
import { createRouterSelector, useQueryParam } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	useGlobalObjectPreviewPanels as usePlatformGlobalObjectPreviewPanels,
	usePreviewPanelIFrameCommunication,
	asGlobalObjectType,
	type GlobalObjectType,
} from '@atlassian/preview-panel-global-objects';
import {
	PreviewPanelsProvider as PlatformPreviewPanelsProvider,
	usePreviewPanelsActions,
	type Panel,
} from '@atlassian/preview-panels-api';
import { CONVERSATION_ASSISTANT_CONTAINER_ID } from '@atlassian/conversation-assistant/utils';
import { useRightSidebarController } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import {
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { HELP_PANEL_ID } from '@atlassian/jira-help-panel-common-constant/src/index.tsx';
import { isNativeIssueViewPreviewPanel } from './isNativeIssueViewPreviewPanel.tsx';
import { LIST_VIEW_ROUTES } from './constants.tsx';
import { useEmbeddedConfluencePanelCommunication } from './useEmbeddedConfluencePanelCommunication.tsx';

type OpenPreviewPanel = (props: {
	url: string;
	ari: string;
	name: string;
	iconUrl?: string;
	parentProduct?: string;
}) => void;
type IsPreviewPanelAvailable = (props: { ari: string }) => boolean;
type Action = 'open' | 'close';
type Event = {
	action: Action;
	panel: Panel;
};
type NotifyCallback = (event: Event) => void;

export const JiraPreviewPanelsContext = createContext<{
	subscribe: (callback: (event: Event) => void) => () => void;
	openPreviewPanel: OpenPreviewPanel;
	isPreviewPanelAvailable: IsPreviewPanelAvailable;
} | null>(null);

/**
 * Only return the route name and path if the route is preview panel enabled.
 * Return as a string to ensure the value is stable and avoid unnecessary re-renders.
 */
export const useEnabledRoute = createRouterSelector<string | null>(({ route, location }) =>
	route.supportsPreviewPanel ? `${route.name}:${location.pathname}` : null,
);

/**
 * Until we have added the option to open preview panel in list view, we will close the preview panels, without deselecting the issue in list view.
 */
export const useIsNotListViewRoute = createRouterSelector<boolean>(({ route }) => {
	return route?.name ? !LIST_VIEW_ROUTES.includes(route.name) : true;
});

const JiraPreviewPanelsProvider = ({
	children,
	subscribe,
}: {
	children: ReactNode;
	subscribe: (callback: NotifyCallback) => () => void;
}) => {
	di(window);
	const cloudId = useCloudId();
	const { close } = usePreviewPanelsActions();
	const enabledRoute = useEnabledRoute();
	const isIssueEmbed = enabledRoute?.split(':')[0] === 'issue-embed';

	let SUPPORT_PANEL_TYPES_MILESTONE_ONE: GlobalObjectType[] = [];

	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);
	const isJSMOrJPDProject =
		projectType === SERVICE_DESK_PROJECT || projectType === PRODUCT_DISCOVERY_PROJECT;

	/**
	 * Update Jira Preview Panel integration for different products
	 * (Confluence, Goals, Team, Project) behind different FGs.
	 * This is to have ability to control different product integrations using FG.
	 * So, that we should be able to turn specific product ON/OFF individually.
	 */
	if (fg('jira-preview-panel-team-integration')) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('identity-team');
	}

	if (fg('jira-preview-panel-goals-integration')) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('townsquare-goal');
	}

	if (fg('jira-preview-panel-confluence-integration')) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('confluence-page');
	}

	if (fg('jira-preview-panel-projects-integration')) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('townsquare-project');
	}

	if (fg('jira_preview_panel_m1_4')) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE.push('jira-issue');
	}

	if (!fg('jira_preview_panel_m_1_rollout') || isJSMOrJPDProject) {
		SUPPORT_PANEL_TYPES_MILESTONE_ONE = [];
	}

	let supportedObjects: GlobalObjectType[] | undefined = SUPPORT_PANEL_TYPES_MILESTONE_ONE;

	// If the route is an issue embed, we can get the supported objects from the URL search params.
	// This allows us to support custom objects in the preview panel for the host application.
	if (isIssueEmbed && typeof window !== 'undefined') {
		const urlSearchParams = new URLSearchParams(window.location.search);
		supportedObjects = urlSearchParams.get('previewPanels')?.split(',')?.map(asGlobalObjectType);
	}

	const { isPreviewPanelAvailable, openPreviewPanel } = usePlatformGlobalObjectPreviewPanels(
		'jira',
		cloudId,
		{
			supportedObjects,
			enabled: enabledRoute !== null,
		},
	);

	usePreviewPanelIFrameCommunication(openPreviewPanel);
	useEmbeddedConfluencePanelCommunication({ closePreviewPanel: close });

	useEffect(
		() => () => {
			// automatically close the preview panel when the route changes
			close();
		},
		[enabledRoute, close],
	);

	const value = useMemo(
		() => ({
			subscribe,
			openPreviewPanel,
			isPreviewPanelAvailable,
		}),
		[subscribe, openPreviewPanel, isPreviewPanelAvailable],
	);

	return (
		<JiraPreviewPanelsContext.Provider value={value}>{children}</JiraPreviewPanelsContext.Provider>
	);
};

export const PreviewPanelsProvider = ({ children }: { children: ReactNode }) => {
	const subscribers = useRef<Set<NotifyCallback>>(new Set());
	const { closeRightSidebar } = useRightSidebarController();
	const [, setSelectedIssue] = useQueryParam('selectedIssue');
	const isNotListView = useIsNotListViewRoute();

	const isNotListViewRef = useRef(isNotListView);
	isNotListViewRef.current = isNotListView;

	const notifySubscribers = useCallback((event: Event) => {
		subscribers.current.forEach((callback) => callback(event));
	}, []);

	const handlePreviewPanelOpen = useCallback(
		(panel: Panel) => {
			// Close Rovo Chat when a preview panel opens
			closeRightSidebar(CONVERSATION_ASSISTANT_CONTAINER_ID);
			// Close Help Panel when a preview panel opens
			if (fg('close_help_panel_on_preview_panel_open')) {
				closeRightSidebar(HELP_PANEL_ID);
			}
			// Close legacy Embedded Confluence Side Panel when a preview panel opens
			globalThis.window?.postMessage({ type: 'CLOSE_EMBEDDED_CONFLUENCE_PANEL' }, '*');

			notifySubscribers({ action: 'open', panel });

			if (!isNativeIssueViewPreviewPanel(panel) && isNotListViewRef.current) {
				setSelectedIssue?.(undefined);
			}
		},
		[closeRightSidebar, notifySubscribers, setSelectedIssue],
	);

	const handlePreviewPanelClose = useCallback(
		(panel: Panel) => {
			notifySubscribers({ action: 'close', panel });
		},
		[notifySubscribers],
	);

	const subscribe = useCallback((callback: NotifyCallback) => {
		subscribers.current.add(callback);

		return () => {
			subscribers.current.delete(callback);
		};
	}, []);

	return (
		<PlatformPreviewPanelsProvider
			onPreviewPanelOpen={handlePreviewPanelOpen}
			onPreviewPanelClose={handlePreviewPanelClose}
		>
			<JiraPreviewPanelsProvider subscribe={subscribe}>{children}</JiraPreviewPanelsProvider>
		</PlatformPreviewPanelsProvider>
	);
};

export const useGlobalObjectPreviewPanels = () => {
	const context = useContext(JiraPreviewPanelsContext);
	if (!context) {
		throw new Error('useGlobalObjectPreviewPanels must be used within a PreviewPanelsProvider');
	}

	const { openPreviewPanel: openGlobalPreviewPanel, isPreviewPanelAvailable } = context;

	const openPreviewPanel = (props: {
		url: string;
		ari: string;
		name: string;
		iconUrl?: string;
	}) => {
		openGlobalPreviewPanel({
			...props,
			parentProduct: 'jira-software',
		});
	};

	return {
		openPreviewPanel,
		isPreviewPanelAvailable,
	};
};

export const usePreviewPanelStateChanged = (callback: NotifyCallback) => {
	const { subscribe } = useContext(JiraPreviewPanelsContext) ?? {};

	useEffect(() => {
		if (!subscribe) return;

		const unsubscribe = subscribe(callback);
		return () => unsubscribe();
	}, [subscribe, callback]);
};
